import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, ChevronRight, ClipboardList, Clock, Eye, Loader2, MapPin, Pencil, Phone, Plus, Search } from "lucide-react";
import { api, errorMessage, get, patch, post } from "@/lib/api";
import { isValidPostcode, normalizePostcode, STATUS_STYLES, WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { ListPager, usePaged } from "@/components/ListPager";
import { useAuth } from "@/context/AuthContext";
import { useAsync } from "@/lib/queries";
import { EmptyState, ErrorState, FieldError, LoadingRows, PageHeader, usePageTitle } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const VISIT_STATUSES = [["scheduled", "Scheduled"], ["in_progress", "In Progress"], ["completed", "Completed"]];
const VULNERABILITY_SECTIONS = [
  {
    title: "Household Composition & Demographics",
    questions: [
      ["children_under_5", "Are there any children under the age of 5 living in the property?"],
      ["state_pension_age", "Is anyone in the household of State Pension age?"],
      ["pregnant_or_newborn", "Are you pregnant or is there a newborn in the home?"],
    ],
  },
  {
    title: "Medical Conditions & Accessibility Needs",
    questions: [
      ["critical_medical_equipment", "Does anyone rely on electricity for critical medical equipment (such as oxygen concentrators, stairlifts, dialysis machines, or nebulisers)?"],
      ["chronic_health_condition", "Does anyone have a chronic illness or long-term health condition?"],
      ["disability_or_learning_or_mental_health", "Is anyone living with a physical disability, learning difficulty, or mental health condition?"],
      ["sensory_or_speech_impairment", "Do you have a hearing, visual, or speech impairment?"],
      ["language_support_needed", "Do you have trouble reading, speaking, or understanding English?"],
      ["reduced_sense_of_smell", "Does anyone have a reduced or missing sense of smell?"],
    ],
  },
  {
    title: "Temporary or Transient Vulnerabilities",
    questions: [
      ["major_life_change", "Have you recently experienced a major life change, such as bereavement, job loss, or returning home after an extended hospital or care home stay?"],
      ["temporary_mobility_restriction", "Are you recovering from a temporary injury or surgery that restricts your mobility?"],
    ],
  },
];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif";
const EMPTY_VISIT = { agent_id: "", customer_name: "", customer_email: "", customer_telephone: "", vulnerability_text: "", vulnerability_answers: {}, postcode: "", full_address: "", visit_type: "" };

const formatTimestamp = (value) => value ? new Date(value).toLocaleString() : "";

const VulnerabilityQuestions = ({ answers = {}, onChange, testId }) => (
  <div className="space-y-5" data-testid={`${testId}-vulnerability-questions`}>
    {VULNERABILITY_SECTIONS.map((section) => (
      <section key={section.title} className="border-t border-slate-100 pt-4 first:border-t-0 first:pt-0">
        <h3 className="text-sm font-semibold text-slate-800">{section.title}</h3>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          {section.questions.map(([key, prompt]) => (
            <div key={key}>
              <Label htmlFor={`${testId}-${key}`}>{prompt}</Label>
              <Select value={answers[key] || "unanswered"} onValueChange={(answer) => {
                const next = { ...answers };
                if (answer === "unanswered") delete next[key];
                else next[key] = answer;
                onChange(next);
              }}>
                <SelectTrigger id={`${testId}-${key}`} className="mt-1.5" data-testid={`${testId}-${key}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unanswered">Choose an answer</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="not_known">Not known</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </section>
    ))}
  </div>
);

const VisitImage = ({ visitId, image }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl;
    api.get(`/pre-warrant-visits/${visitId}/images/${image.id}`, { responseType: "blob" }).then((response) => {
      objectUrl = URL.createObjectURL(response.data);
      if (active) setUrl(objectUrl);
      else URL.revokeObjectURL(objectUrl);
    }).catch(() => { if (active) setFailed(true); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visitId, image.id]);

  return (
    <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50" data-testid={`visit-image-${image.id}`}>
      {url ? <img src={url} alt={image.filename || "Pre-warrant visit"} className="h-full w-full object-cover" />
        : failed ? <span className="px-1 text-center text-[10px] text-slate-500">Unavailable</span>
          : <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
    </div>
  );
};

const VisitCard = ({ visit, onStatusChange, onImagesChanged, statusBusy }) => {
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [details, setDetails] = useState(visit.details || "");
  const [savingDetails, setSavingDetails] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [customerForm, setCustomerForm] = useState({
    customer_name: visit.customer_name || "",
    customer_email: visit.customer_email || "",
    customer_telephone: visit.customer_telephone || "",
    full_address: visit.full_address || "",
    postcode: visit.postcode || "",
    vulnerability_text: visit.vulnerability_text || "",
    vulnerability_answers: visit.vulnerability_answers || {},
  });

  useEffect(() => {
    setDetails(visit.details || "");
  }, [visit.id, visit.details]);

  useEffect(() => {
    setCustomerForm({
      customer_name: visit.customer_name || "",
      customer_email: visit.customer_email || "",
      customer_telephone: visit.customer_telephone || "",
      full_address: visit.full_address || "",
      postcode: visit.postcode || "",
      vulnerability_text: visit.vulnerability_text || "",
      vulnerability_answers: visit.vulnerability_answers || {},
    });
  }, [visit.id, visit.customer_name, visit.customer_email, visit.customer_telephone, visit.full_address, visit.postcode, visit.vulnerability_text, visit.vulnerability_answers]);

  const uploadImages = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const tooBig = files.find((file) => file.size > MAX_IMAGE_BYTES);
    if (tooBig) return toast.error(`${tooBig.name} is larger than 10 MB`);
    setUploading(true);
    try {
      for (const file of files) {
        const body = new FormData();
        body.append("file", file);
        await api.post(`/pre-warrant-visits/${visit.id}/images`, body);
      }
      toast.success(files.length === 1 ? "Visit image uploaded" : `${files.length} visit images uploaded`);
      onImagesChanged();
    } catch (error) {
      toast.error(errorMessage(error));
      onImagesChanged();
    } finally {
      setUploading(false);
    }
  };

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      await patch(`/pre-warrant-visits/${visit.id}/details`, { details });
      toast.success("Visit details saved");
      onImagesChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSavingDetails(false);
    }
  };

  const saveCustomerDetails = async () => {
    const customerName = customerForm.customer_name.trim();
    const customerEmail = customerForm.customer_email.trim();
    const customerTelephone = customerForm.customer_telephone.trim();
    if (customerName.length < 2) return setCustomerError("Enter the customer's name");
    if (customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) return setCustomerError("Enter a valid email address");
    if (customerTelephone && !/^[0-9+()\-\s]{6,40}$/.test(customerTelephone)) return setCustomerError("Enter a valid telephone number");
    if (!isValidPostcode(customerForm.postcode)) return setCustomerError("Enter a valid UK postcode");
    setCustomerError("");
    setSavingCustomer(true);
    try {
      await patch(`/pre-warrant-visits/${visit.id}/customer-details`, {
        customer_name: customerName,
        customer_email: customerEmail || null,
        customer_telephone: customerTelephone || null,
        full_address: customerForm.full_address.trim(),
        postcode: normalizePostcode(customerForm.postcode),
        vulnerability_text: customerForm.vulnerability_text.trim() || null,
        vulnerability_answers: customerForm.vulnerability_answers || {},
      });
      toast.success("Customer details updated");
      setEditingCustomer(false);
      onImagesChanged();
    } catch (error) {
      setCustomerError(errorMessage(error));
    } finally {
      setSavingCustomer(false);
    }
  };

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`pre-warrant-visit-${visit.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-data text-xs uppercase tracking-[0.16em] text-blue-700">Pre Warrant Visit</p>
          <h1 className="font-display mt-1 text-2xl font-extrabold text-slate-900" data-testid={`visit-customer-${visit.id}`}>{visit.customer_name}</h1>
          <p className="mt-1 text-sm text-slate-600">{visit.visit_type} · {visit.postcode}</p>
        </div>
        <StatusBadge status={visit.status} testId={`visit-detail-status-${visit.id}`} />
      </div>
      <div className="mt-5 border-t border-slate-100 pt-4">
        <div className="mt-2 grid grid-cols-3 gap-2 sm:max-w-[480px]" role="group" aria-label="Visit status" data-testid={`visit-status-${visit.id}`}>
          {VISIT_STATUSES.map(([value, label]) => {
            const active = visit.status === value;
            return <button key={value} type="button" disabled={statusBusy || active} onClick={() => onStatusChange(visit, value)} aria-pressed={active}
              className={`inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${STATUS_STYLES[value] || STATUS_STYLES.scheduled} ${active ? "ring-2 ring-offset-1 ring-slate-300" : "hover:brightness-95"} disabled:cursor-default`}
              data-testid={`visit-status-${visit.id}-${value}`}><span className="h-1.5 w-1.5 rounded-full bg-[var(--dot)]" />{label}</button>;
          })}
        </div>
      </div>
      {(visit.in_progress_at || visit.completed_at) && <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 border-t border-slate-100 pt-3 text-xs text-slate-600">
        {visit.in_progress_at && <span data-testid={`visit-in-progress-time-${visit.id}`}>In Progress: {formatTimestamp(visit.in_progress_at)}</span>}
        {visit.completed_at && <span data-testid={`visit-completed-time-${visit.id}`}>Completed: {formatTimestamp(visit.completed_at)}</span>}
      </div>}
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <p className="font-data text-[10px] uppercase tracking-[0.16em] text-slate-500">Email</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{visit.customer_email ? <a href={`mailto:${visit.customer_email}`} className="text-blue-700 hover:underline">{visit.customer_email}</a> : "—"}</p>
        </div>
        <div>
          <p className="font-data text-[10px] uppercase tracking-[0.16em] text-slate-500">Telephone</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{visit.customer_telephone ? <a href={`tel:${visit.customer_telephone}`} className="text-blue-700 hover:underline">{visit.customer_telephone}</a> : "—"}</p>
        </div>
        <div>
          <p className="font-data text-[10px] uppercase tracking-[0.16em] text-slate-500">Address</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{visit.full_address || "—"}<span className="block font-normal text-slate-600">{visit.postcode}</span></p>
        </div>
        <div>
          <p className="font-data text-[10px] uppercase tracking-[0.16em] text-slate-500">Assigned Agent</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{visit.agent_name || "Agent"}</p>
        </div>
        <div>
          <p className="font-data text-[10px] uppercase tracking-[0.16em] text-slate-500">Created</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{formatTimestamp(visit.created_at)}</p>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        {!editingCustomer && <Button type="button" size="sm" variant="outline" onClick={() => setEditingCustomer(true)} data-testid={`edit-customer-details-${visit.id}`}><Pencil className="mr-1 h-4 w-4" /> Edit customer details</Button>}
      </div>
      {editingCustomer && <div className="mt-5 border-t border-slate-100 pt-4" data-testid={`customer-details-editor-${visit.id}`}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor={`visit-edit-name-${visit.id}`}>Customer name *</Label>
            <Input id={`visit-edit-name-${visit.id}`} value={customerForm.customer_name} maxLength={120} onChange={(event) => setCustomerForm({ ...customerForm, customer_name: event.target.value })} className="mt-1.5" data-testid={`visit-edit-name-${visit.id}`} />
          </div>
          <div>
            <Label htmlFor={`visit-edit-email-${visit.id}`}>Customer email</Label>
            <Input id={`visit-edit-email-${visit.id}`} type="email" value={customerForm.customer_email} maxLength={254} onChange={(event) => setCustomerForm({ ...customerForm, customer_email: event.target.value })} className="mt-1.5" data-testid={`visit-edit-email-${visit.id}`} />
          </div>
          <div>
            <Label htmlFor={`visit-edit-phone-${visit.id}`}>Customer telephone</Label>
            <Input id={`visit-edit-phone-${visit.id}`} type="tel" value={customerForm.customer_telephone} maxLength={40} onChange={(event) => setCustomerForm({ ...customerForm, customer_telephone: event.target.value })} className="mt-1.5" data-testid={`visit-edit-phone-${visit.id}`} />
          </div>
          <div>
            <Label htmlFor={`visit-edit-address-${visit.id}`}>Full address</Label>
            <Input id={`visit-edit-address-${visit.id}`} value={customerForm.full_address} maxLength={300} onChange={(event) => setCustomerForm({ ...customerForm, full_address: event.target.value })} className="mt-1.5" data-testid={`visit-edit-address-${visit.id}`} />
          </div>
          <div>
            <Label htmlFor={`visit-edit-postcode-${visit.id}`}>Postcode *</Label>
            <Input id={`visit-edit-postcode-${visit.id}`} value={customerForm.postcode} maxLength={9} onChange={(event) => setCustomerForm({ ...customerForm, postcode: event.target.value })} className="mt-1.5 uppercase" data-testid={`visit-edit-postcode-${visit.id}`} />
          </div>
        </div>
        <section className="mt-5 space-y-4 rounded-lg border border-slate-200 bg-white p-4" data-testid={`visit-edit-vulnerability-box-${visit.id}`}>
          <h2 className="text-sm font-semibold text-slate-900">Questions</h2>
          <VulnerabilityQuestions answers={customerForm.vulnerability_answers} onChange={(vulnerability_answers) => setCustomerForm({ ...customerForm, vulnerability_answers })} testId={`visit-edit-${visit.id}`} />
          <div>
            <Label htmlFor={`visit-edit-vulnerability-${visit.id}`}>Additional vulnerability details</Label>
            <Textarea id={`visit-edit-vulnerability-${visit.id}`} value={customerForm.vulnerability_text} onChange={(event) => setCustomerForm({ ...customerForm, vulnerability_text: event.target.value })} rows={3} maxLength={2000} className="mt-1.5" data-testid={`visit-edit-vulnerability-${visit.id}`} />
          </div>
        </section>
        {customerError && <p role="alert" className="mt-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700">{customerError}</p>}
        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" variant="outline" disabled={savingCustomer} onClick={() => { setCustomerError(""); setEditingCustomer(false); }}>Cancel</Button>
          <Button type="button" disabled={savingCustomer} className="bg-blue-700 hover:bg-blue-800" onClick={saveCustomerDetails} data-testid={`save-customer-details-${visit.id}`}>
            {savingCustomer && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save customer details
          </Button>
        </div>
      </div>}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <Label htmlFor={`visit-details-${visit.id}`}>Visit Details</Label>
        <Textarea id={`visit-details-${visit.id}`} value={details} onChange={(event) => setDetails(event.target.value)} rows={3} maxLength={4000} className="mt-1.5" data-testid={`visit-details-${visit.id}`} />
        <div className="mt-2 flex justify-end">
          <Button type="button" size="sm" variant="outline" disabled={savingDetails || details === (visit.details || "")} onClick={saveDetails} data-testid={`save-visit-details-${visit.id}`}>
            {savingDetails && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save details
          </Button>
        </div>
      </div>
      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">Visit Images</h3>
          <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => input.current?.click()} data-testid={`visit-upload-${visit.id}`}>
            {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />}
            {uploading ? "Uploading" : "Upload images"}
          </Button>
          <input ref={input} type="file" accept={IMAGE_ACCEPT} multiple hidden onChange={uploadImages} data-testid={`visit-image-input-${visit.id}`} />
        </div>
        {visit.images?.length > 0 ? <div className="mt-3 flex flex-wrap gap-2">{visit.images.map((image) => <VisitImage key={image.id} visitId={visit.id} image={image} />)}</div>
          : <p className="mt-2 text-sm text-slate-500">No images uploaded.</p>}
      </div>
    </article>
  );
};

const VisitQuestionsCard = ({ visit }) => {
  const hasAnswers = Object.keys(visit.vulnerability_answers || {}).length > 0;
  if (!hasAnswers && !visit.vulnerability_text) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`visit-vulnerability-summary-${visit.id}`}>
      <h2 className="font-display text-lg font-bold text-slate-900">Questions</h2>
      {VULNERABILITY_SECTIONS.map((section) => {
        const answered = section.questions.filter(([key]) => visit.vulnerability_answers?.[key]);
        return answered.length > 0 && <div key={section.title} className="mt-4 border-t border-slate-100 pt-4 first:border-t-0 first:pt-0">
          <h3 className="text-sm font-semibold text-slate-900">{section.title}</h3>
          <dl className="mt-2 space-y-2">
            {answered.map(([key, prompt]) => <div key={key} className="grid gap-1 text-sm sm:grid-cols-[1fr_auto]">
              <dt className="text-slate-700">{prompt}</dt>
              <dd className="font-semibold text-slate-900">{visit.vulnerability_answers[key] === "not_known" ? "Not known" : visit.vulnerability_answers[key] === "yes" ? "Yes" : "No"}</dd>
            </div>)}
          </dl>
        </div>;
      })}
      {visit.vulnerability_text && <p className="mt-4 border-t border-slate-100 pt-4 whitespace-pre-wrap text-sm text-slate-700">Additional details: {visit.vulnerability_text}</p>}
    </section>
  );
};

export const PreWarrantVisitForm = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isManager = profile?.role === "manager";
  const enabledTypes = profile?.company?.enabled_user_types ?? WORKER_ROLE_OPTIONS.map(([role]) => role);
  const visitTypes = profile?.company?.visit_types ?? [];
  const { data: existingVisit, loading: loadingVisit, error: loadingVisitError, reload: reloadExistingVisit } = useAsync(() => id ? get(`/pre-warrant-visits/${id}`) : Promise.resolve(null), [id]);
  const { data: teamMembers, loading: loadingTeam } = useAsync(() => get("/locksmiths"), []);
  const agents = (teamMembers || []).filter((member) => (member.user_types || [member.role]).includes("agent") && member.membership_status === "active");
  const availableVisitTypes = existingVisit?.visit_type && !visitTypes.includes(existingVisit.visit_type) ? [...visitTypes, existingVisit.visit_type] : visitTypes;
  const [form, setForm] = useState(EMPTY_VISIT);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (existingVisit) setForm({
      ...EMPTY_VISIT,
      ...existingVisit,
      customer_email: existingVisit.customer_email || "",
      customer_telephone: existingVisit.customer_telephone || "",
      vulnerability_text: existingVisit.vulnerability_text || "",
      vulnerability_answers: existingVisit.vulnerability_answers || {},
    });
  }, [existingVisit]);

  if (!isManager || !enabledTypes.includes("agent")) return <Navigate to="/manager" replace />;
  if (loadingVisit && id && !existingVisit) return <LoadingRows rows={3} />;
  if (loadingVisitError && !existingVisit) return <ErrorState message={loadingVisitError} onRetry={reloadExistingVisit} />;

  const save = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    const customerEmail = (form.customer_email || "").trim();
    const customerTelephone = (form.customer_telephone || "").trim();
    const vulnerabilityText = (form.vulnerability_text || "").trim();
    if (!agents.some((agent) => agent.id === form.agent_id)) nextErrors.agent_id = "Choose an active Agent";
    if (form.customer_name.trim().length < 2) nextErrors.customer_name = "Enter the customer's name";
    if (customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) nextErrors.customer_email = "Enter a valid email address";
    if (customerTelephone && !/^[0-9+()\-\s]{6,40}$/.test(customerTelephone)) nextErrors.customer_telephone = "Enter a valid telephone number";
    if (!isValidPostcode(form.postcode)) nextErrors.postcode = "Enter a valid UK postcode";
    if (!availableVisitTypes.includes(form.visit_type)) nextErrors.visit_type = "Choose a Visit Type";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setBusy(true);
    try {
      const payload = {
        agent_id: form.agent_id,
        customer_name: form.customer_name.trim(),
        customer_email: customerEmail || null,
        customer_telephone: customerTelephone || null,
        vulnerability_text: vulnerabilityText || null,
        vulnerability_answers: form.vulnerability_answers || {},
        postcode: normalizePostcode(form.postcode),
        full_address: form.full_address.trim(),
        visit_type: form.visit_type,
      };
      if (id) await patch(`/pre-warrant-visits/${id}`, payload);
      else await post("/pre-warrant-visits", payload);
      toast.success(id ? "Pre Warrant Visit updated" : "Pre Warrant Visit added");
      navigate("/manager/pre-warrant-visits");
    } catch (error) {
      setErrors({ form: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pre-warrant-visit-form-page">
      <Link to={id ? `/manager/pre-warrant-visits/${id}` : "/manager/pre-warrant-visits"} className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-testid="visit-form-back-link">Back to Pre Warrant Visits</Link>
      <PageHeader eyebrow={id ? "Edit" : "Create"} title={id ? "Edit Pre Warrant Visit" : "Pre Warrant Visit"} description="Record customer details, address, and visit type." />
      <form onSubmit={save} noValidate className="space-y-6">
        <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-sm font-semibold text-slate-900">Warrant Details</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Visit Type *</Label>
              <Select value={form.visit_type} onValueChange={(visit_type) => setForm({ ...form, visit_type })} disabled={!availableVisitTypes.length}>
                <SelectTrigger className="mt-1.5" data-testid="visit-type-select"><SelectValue placeholder={availableVisitTypes.length ? "Choose a Visit Type" : "Configure Visit Types in Company settings"} /></SelectTrigger>
                <SelectContent>{availableVisitTypes.map((visitType) => <SelectItem key={visitType} value={visitType}>{visitType}</SelectItem>)}</SelectContent>
              </Select>
              <FieldError message={errors.visit_type} testId="visit-type-error" />
            </div>
            <div>
              <Label>Agent *</Label>
              <Select value={form.agent_id} onValueChange={(agent_id) => setForm({ ...form, agent_id })} disabled={loadingTeam || !agents.length}>
                <SelectTrigger className="mt-1.5" data-testid="visit-agent-select"><SelectValue placeholder={loadingTeam ? "Loading Agents" : agents.length ? "Choose an Agent" : "No active Agents"} /></SelectTrigger>
                <SelectContent>{agents.map((agent) => <SelectItem key={agent.id} value={agent.id}>{agent.full_name || agent.email}</SelectItem>)}</SelectContent>
              </Select>
              <FieldError message={errors.agent_id} testId="visit-agent-error" />
            </div>
          </div>
        </section>
        <div className="grid gap-5 lg:grid-cols-2">
          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-slate-900">Customer Details</legend>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="visit-customer-name">Customer name *</Label>
                  <Input id="visit-customer-name" value={form.customer_name} maxLength={120} onChange={(event) => setForm({ ...form, customer_name: event.target.value })} className="mt-1.5" data-testid="visit-customer-name" />
                  <FieldError message={errors.customer_name} testId="visit-customer-name-error" />
                </div>
                <div>
                  <Label htmlFor="visit-customer-telephone">Customer telephone</Label>
                  <Input id="visit-customer-telephone" type="tel" value={form.customer_telephone} maxLength={40} onChange={(event) => setForm({ ...form, customer_telephone: event.target.value })} className="mt-1.5" data-testid="visit-customer-telephone" />
                  <FieldError message={errors.customer_telephone} testId="visit-customer-telephone-error" />
                </div>
                <div>
                  <Label htmlFor="visit-customer-email">Customer email</Label>
                  <Input id="visit-customer-email" type="email" value={form.customer_email} maxLength={254} onChange={(event) => setForm({ ...form, customer_email: event.target.value })} className="mt-1.5" data-testid="visit-customer-email" />
                  <FieldError message={errors.customer_email} testId="visit-customer-email-error" />
                </div>
              </div>
            </fieldset>
          </section>
          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-slate-900">Address</legend>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="visit-address">Full address</Label>
                  <Input id="visit-address" value={form.full_address} maxLength={300} onChange={(event) => setForm({ ...form, full_address: event.target.value })} className="mt-1.5" data-testid="visit-address" />
                </div>
                <div>
                  <Label htmlFor="visit-postcode">Postcode *</Label>
                  <Input id="visit-postcode" value={form.postcode} maxLength={9} onChange={(event) => setForm({ ...form, postcode: event.target.value })} className="mt-1.5 uppercase" data-testid="visit-postcode" />
                  <FieldError message={errors.postcode} testId="visit-postcode-error" />
                </div>
              </div>
            </fieldset>
          </section>
        </div>
        <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="visit-create-vulnerability-box">
          <h2 className="text-sm font-semibold text-slate-900">Questions</h2>
          <VulnerabilityQuestions answers={form.vulnerability_answers} onChange={(vulnerability_answers) => setForm({ ...form, vulnerability_answers })} testId="visit-create" />
          <div>
            <Label htmlFor="visit-vulnerability">Additional vulnerability details</Label>
            <Textarea id="visit-vulnerability" rows={3} value={form.vulnerability_text} maxLength={2000} onChange={(event) => setForm({ ...form, vulnerability_text: event.target.value })} className="mt-1.5" data-testid="visit-vulnerability" />
          </div>
        </section>
        <section className="min-h-20 rounded-xl border border-slate-200 bg-white p-6" data-testid="visit-account-details">
          <h2 className="text-sm font-semibold text-slate-900">Account Details</h2>
        </section>
        {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-slate-700" data-testid="visit-form-error">{errors.form}</p>}
        <div className="sticky bottom-0 -mx-4 flex justify-end gap-2 border-t border-slate-200 bg-white/90 p-4 backdrop-blur sm:-mx-6 lg:-mx-8">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={busy || !availableVisitTypes.length} className="bg-blue-700 hover:bg-blue-800" data-testid="visit-save-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{id ? "Save changes" : "Add Visit"}
          </Button>
        </div>
      </form>
    </div>
  );
};

const AgentVisitCard = ({ visit }) => (
  <Link to={`/jobs/pre-warrant-visits/${visit.id}`} data-testid={`agent-visit-card-${visit.id}`} className="block rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md active:bg-slate-50">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-data text-xs uppercase tracking-wider text-slate-500">{visit.visit_type}</p>
        <p className="font-display mt-1 text-lg font-bold text-slate-900">{visit.customer_name}</p>
      </div>
      <StatusBadge status={visit.status} testId={`agent-visit-status-${visit.id}`} />
    </div>
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{visit.full_address ? `${visit.full_address}, ` : ""}{visit.postcode}</span>
      {visit.customer_telephone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" />{visit.customer_telephone}</span>}
    </div>
    <div className="mt-3 flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" />Updated {formatTimestamp(visit.updated_at)}</span>
      <ChevronRight className="h-5 w-5 text-slate-400" />
    </div>
  </Link>
);

export default function PreWarrantVisits() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const isManager = profile?.role === "manager";
  const userTypes = profile?.user_types?.length ? profile.user_types : [profile?.role];
  const isAgent = userTypes.includes("agent");
  const enabledTypes = profile?.company?.enabled_user_types ?? WORKER_ROLE_OPTIONS.map(([role]) => role);
  const canAccess = enabledTypes.includes("agent") && (isManager || isAgent);
  const visitTypes = profile?.company?.visit_types ?? [];
  const { data: visits, loading, error, reload } = useAsync(() => canAccess ? get("/pre-warrant-visits") : Promise.resolve([]), [canAccess]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const rows = useMemo(() => (visits || []).filter((visit) => {
    const matchesStatus = status === "all" || visit.status === status;
    const haystack = [visit.customer_name, visit.customer_email, visit.customer_telephone, visit.full_address, visit.postcode, visit.visit_type, visit.agent_name].join(" ").toLowerCase();
    return matchesStatus && haystack.includes(query.toLowerCase().trim());
  }), [visits, query, status]);
  const pager = usePaged(rows);

  if (!canAccess) return <Navigate to={isManager ? "/manager" : "/jobs"} replace />;

  return (
    <div className="space-y-6" data-testid="pre-warrant-visits-page">
      <PageHeader eyebrow="Operations" title="Pre Warrant Visits" description="Customer visits before a warrant run."
        actions={isManager && <Button asChild className="bg-blue-700 hover:bg-blue-800" data-testid="add-pre-warrant-visit-button"><Link to="/manager/pre-warrant-visits/new"><Plus className="mr-1 h-4 w-4" /> Add Visit</Link></Button>} />
      {isManager && !visitTypes.length && <p className="text-sm text-slate-600">Configure Visit Types in <Link to="/manager/profile" className="font-medium text-blue-700 hover:underline">Company settings</Link> before adding a visit.</p>}
      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_200px]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search customer, address or Visit Type" aria-label="Search visits" className="pl-9" data-testid="pre-warrant-visits-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger aria-label="Filter by status" data-testid="pre-warrant-visits-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {VISIT_STATUSES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !visits && <LoadingRows rows={3} />}
      {visits && !rows.length && <EmptyState icon={ClipboardList} title={visits.length ? "No visits found" : "No pre-warrant visits yet"} description={visits.length ? "Adjust your search or status filter." : isManager ? "Add a visit to get started." : "There are no visits assigned to you."} testId="pre-warrant-visits-empty" />}
      {rows.length > 0 && !isManager && <div className="space-y-3" data-testid="agent-pre-warrant-visit-cards">
        {pager.items.map((visit) => <AgentVisitCard key={visit.id} visit={visit} />)}
      </div>}
      {rows.length > 0 && isManager && <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <Table data-testid="pre-warrant-visits-table">
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Customer</TableHead><TableHead>Address</TableHead><TableHead>Visit Type</TableHead>
            {isManager && <TableHead>Assigned Agent</TableHead>}
            <TableHead>Status</TableHead><TableHead>Updated</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pager.items.map((visit) => {
              const detailsPath = `${isManager ? "/manager" : "/jobs"}/pre-warrant-visits/${visit.id}`;
              return <TableRow key={visit.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(detailsPath)} data-testid={`pre-warrant-visit-row-${visit.id}`}>
                <TableCell className="font-medium"><Link to={detailsPath} className="text-blue-800 hover:underline" onClick={(event) => event.stopPropagation()}>{visit.customer_name}</Link></TableCell>
                <TableCell><div>{visit.full_address || "—"}</div><div className="font-data text-xs text-slate-500">{visit.postcode}</div></TableCell>
                <TableCell>{visit.visit_type}</TableCell>
                {isManager && <TableCell>{visit.agent_name}</TableCell>}
                <TableCell><StatusBadge status={visit.status} testId={`visit-list-status-${visit.id}`} /></TableCell>
                <TableCell className="whitespace-nowrap text-sm text-slate-600">{formatTimestamp(visit.updated_at)}</TableCell>
                <TableCell className="text-right" onClick={(event) => event.stopPropagation()}>
                  <Button size="icon" variant="ghost" aria-label="View visit" asChild data-testid={`view-pre-warrant-visit-${visit.id}`}><Link to={detailsPath}><Eye className="h-4 w-4" /></Link></Button>
                </TableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </div>}
      {rows.length > 0 && <ListPager pager={pager} label="visits" testId="pre-warrant-visits-pager" />}
    </div>
  );
}

export const PreWarrantVisitDetail = ({ isManager = false }) => {
  const { id } = useParams();
  usePageTitle("Pre Warrant Visit");
  const { profile } = useAuth();
  const userTypes = profile?.user_types?.length ? profile.user_types : [profile?.role];
  const enabledTypes = profile?.company?.enabled_user_types ?? WORKER_ROLE_OPTIONS.map(([role]) => role);
  const canAccess = enabledTypes.includes("agent") && (isManager ? profile?.role === "manager" : userTypes.includes("agent"));
  const backTo = `${isManager ? "/manager" : "/jobs"}/pre-warrant-visits`;
  const { data: visit, loading, error, reload } = useAsync(() => canAccess ? get(`/pre-warrant-visits/${id}`) : Promise.resolve(null), [id, canAccess]);
  const [statusBusy, setStatusBusy] = useState(false);

  const updateStatus = async (currentVisit, status) => {
    setStatusBusy(true);
    try {
      await patch(`/pre-warrant-visits/${currentVisit.id}/status`, { status });
      toast.success("Visit status updated");
      reload();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setStatusBusy(false);
    }
  };

  if (!canAccess) return <Navigate to={isManager ? "/manager" : "/jobs"} replace />;

  return (
    <div className="space-y-6" data-testid="pre-warrant-visit-detail-page">
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !visit && <LoadingRows rows={3} />}
      {visit && <>
        <div className="flex items-center justify-between gap-2">
          <Link to={backTo} className="inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-testid="visit-detail-back-link"><ArrowLeft className="h-4 w-4" /> Back</Link>
          {isManager && <Button asChild variant="outline" size="sm" data-testid="edit-pre-warrant-visit-button"><Link to={`${backTo}/${visit.id}/edit`}><Pencil className="mr-1 h-4 w-4" /> Edit Visit</Link></Button>}
        </div>
        <VisitCard visit={visit} onStatusChange={updateStatus} onImagesChanged={reload} statusBusy={statusBusy} />
        <VisitQuestionsCard visit={visit} />
      </>}
    </div>
  );
};